import { useSearchParams } from "react-router-dom";
import { CaseView } from "./constants/page";
import { ALL_FILTER } from "./constants/runs";

/**
 * The page's two views, one per API resource: `eval-runs` and `eval-cases`. There
 * is no third — a run is launched with parameters that become fields on the run
 * record, so "run configuration" has nothing standing behind it.
 */
export const EvalsTab = {
  RUNS: "runs",
  CASES: "cases",
} as const;
export type EvalsTab = (typeof EvalsTab)[keyof typeof EvalsTab];

const TAB_PARAM = "tab";
const CASE_VIEW_PARAM = "view";
const TAGS_PARAM = "tags";
const STATUS_PARAM = "status";
const QUERY_PARAM = "q";
const DATASETS_PARAM = "datasets";
const AGENTS_PARAM = "agents";
const CONFIG_PARAM = "config";

const TAB_VALUES: string[] = [EvalsTab.RUNS, EvalsTab.CASES];
const CASE_VIEW_VALUES: string[] = [CaseView.HEALTH, CaseView.CONFIG];

export interface EvalFilters {
  tab: EvalsTab;
  /** Which column set the cases table shows. Shared so a link carries it. */
  caseView: CaseView;
  /**
   * Case-selector tags, sent to the API as repeatable `tags` params. The filter
   * is containment, so a case must carry every tag listed — an AND, not an OR.
   */
  tags: string[];
  /**
   * Dataset ids a case must touch. Applied client-side — the case list route
   * filters only by tag — and it narrows *cases*, so it moves the grid's rows.
   */
  datasets: string[];
  /**
   * Agent ids a run must have evaluated. Client-side, and it narrows *runs*, so it
   * moves the grid's columns — kept separate from the dataset filter because a
   * single "scope" control would hide which axis a reviewer just moved.
   */
  agents: string[];
  /** Run lifecycle status, applied client-side; `ALL_FILTER` means unset. */
  status: string;
  /**
   * The judge configuration a run was judged under, keyed the way
   * `runConfigKey` keys it; `ALL_FILTER` means unset. Client-side, narrows
   * *runs* to one comparable configuration.
   */
  config: string;
  /** Free-text match over case names and descriptions, applied client-side. */
  query: string;
  hasActiveFilters: boolean;
  setTab: (tab: EvalsTab) => void;
  setCaseView: (view: CaseView) => void;
  setTags: (tags: string[]) => void;
  setDatasets: (datasetIds: string[]) => void;
  setAgents: (agentIds: string[]) => void;
  setStatus: (status: string) => void;
  setConfig: (config: string) => void;
  setQuery: (query: string) => void;
  clearFilters: () => void;
}

/**
 * Filter and tab state, held in the URL rather than in component state, so a
 * reviewer who finds a regression sends the link rather than a description of
 * which dropdowns to set. Tab and filters share one hook because the filter row
 * scopes both views, and splitting them would let the two disagree about the URL.
 */
export const useEvalFilters = (): EvalFilters => {
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get(TAB_PARAM);
  const tab = (TAB_VALUES.includes(tabParam ?? "") ? tabParam : EvalsTab.RUNS) as EvalsTab;
  const caseViewParam = searchParams.get(CASE_VIEW_PARAM);
  const caseView = (
    CASE_VIEW_VALUES.includes(caseViewParam ?? "") ? caseViewParam : CaseView.HEALTH
  ) as CaseView;
  const tags = searchParams.getAll(TAGS_PARAM);
  const datasets = searchParams.getAll(DATASETS_PARAM);
  const agents = searchParams.getAll(AGENTS_PARAM);
  const status = searchParams.get(STATUS_PARAM) ?? ALL_FILTER;
  const config = searchParams.get(CONFIG_PARAM) ?? ALL_FILTER;
  const query = searchParams.get(QUERY_PARAM) ?? "";

  // `replace` keeps a filter tweak out of the history stack; only the tab
  // switch is a navigation a reviewer would expect Back to undo.
  const update = (
    mutate: (next: URLSearchParams) => void,
    { replace = true }: { replace?: boolean } = {}
  ) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        mutate(next);
        return next;
      },
      { replace }
    );
  };

  const setRepeatable = (param: string, values: string[]) =>
    update((next) => {
      next.delete(param);
      for (const value of values) {
        next.append(param, value);
      }
    });

  const setSingle = (
    param: string,
    value: string,
    unsetWhen: string,
    options?: { replace?: boolean }
  ) =>
    update((next) => {
      if (value === unsetWhen) {
        next.delete(param);
      } else {
        next.set(param, value);
      }
    }, options);

  return {
    tab,
    caseView,
    tags,
    datasets,
    agents,
    status,
    config,
    query,
    hasActiveFilters:
      tags.length > 0 ||
      datasets.length > 0 ||
      agents.length > 0 ||
      status !== ALL_FILTER ||
      config !== ALL_FILTER ||
      query !== "",
    setTab: (nextTab) => setSingle(TAB_PARAM, nextTab, EvalsTab.RUNS, { replace: false }),
    // A column switch is not a navigation: Back should undo the filter or tab a
    // reviewer changed, not step through which columns they were reading.
    setCaseView: (nextView) => setSingle(CASE_VIEW_PARAM, nextView, CaseView.HEALTH),
    setTags: (nextTags) => setRepeatable(TAGS_PARAM, nextTags),
    setDatasets: (nextDatasets) => setRepeatable(DATASETS_PARAM, nextDatasets),
    setAgents: (nextAgents) => setRepeatable(AGENTS_PARAM, nextAgents),
    setStatus: (nextStatus) => setSingle(STATUS_PARAM, nextStatus, ALL_FILTER),
    setConfig: (nextConfig) => setSingle(CONFIG_PARAM, nextConfig, ALL_FILTER),
    setQuery: (nextQuery) => setSingle(QUERY_PARAM, nextQuery, ""),
    clearFilters: () =>
      update((next) => {
        next.delete(TAGS_PARAM);
        next.delete(DATASETS_PARAM);
        next.delete(AGENTS_PARAM);
        next.delete(STATUS_PARAM);
        next.delete(CONFIG_PARAM);
        next.delete(QUERY_PARAM);
      }),
  };
};
